import { useState, useEffect } from 'react';
import { Database, X, Plus, Upload, Check, AlertCircle, Loader2, Trash2, FilePlus, Server, FileSpreadsheet, Link, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '@/components/UI/Button';
import { Input } from '@/components/UI/input';
import { Label } from '@/components/UI/label';
import { Textarea } from '@/components/UI/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DataSource,
  DataSourceCreateRequest,
  DataSourceTestResponse,
  startDataSourceAnalysis as defaultStartDataSourceAnalysis,
  getActiveAnalyses as defaultGetActiveAnalyses,
  getLatestAnalyses as defaultGetLatestAnalyses,
  getColumnDescriptions as defaultGetColumnDescriptions,
  DataSourceAnalysisStatus,
  ColumnDescription,
} from '@/services/api';
import { api, uuidToInt } from '@/services/api';
import { sapOAuthService } from '@/services/sapOAuthService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/UI/dropdown-menu';

export interface DataSourceApiFunctions {
  listDataSources: (userId: string) => Promise<DataSource[]>;
  createDataSource: (request: DataSourceCreateRequest) => Promise<DataSource>;
  uploadExcelDataSource: (userId: string, name: string, file: File) => Promise<DataSource>;
  uploadMultipleExcelDataSources: (userId: string, name: string, files: File[]) => Promise<DataSource>;
  activateDataSource: (userId: string, dataSourceId: number) => Promise<DataSource>;
  deactivateDataSource: (userId: string, dataSourceId: number) => Promise<{ success: boolean; message: string }>;
  deleteDataSource: (userId: string, dataSourceId: number) => Promise<{ success: boolean; message: string }>;
  appendExcelSheets: (dataSourceId: number, userId: string, file: File) => Promise<any>;
  testDataSource: (userId: string, dataSourceId?: number) => Promise<DataSourceTestResponse>;
  testAllDataSources: (userId: string) => Promise<Record<number, DataSourceTestResponse>>;
  getActiveDataSource: (userId: string) => Promise<DataSource | null>;
  getExcelSheets: (dataSourceId: number, userId: string) => Promise<any>;
  // Accept either the canonical UUID string (preferred) or a synthetic
  // numeric synthetic id; the SmartCHG endpoint keys on UUID, and the
  // helper coerces accordingly.
  startDataSourceAnalysis: (dataSourceId: string | number, userId: string, description: string) => Promise<{ analysis_id: string; datasource_id?: string; status: string }>;
  getActiveAnalyses: () => Promise<DataSourceAnalysisStatus[]>;
  getLatestAnalyses: () => Promise<DataSourceAnalysisStatus[]>;
  getColumnDescriptions: (analysisId: string | number) => Promise<ColumnDescription[]>;
}

function compactRecord(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

function toSmartChgType(type: string): string {
  return type === 'sap' ? 'sap_datasphere' : type;
}

function fromSmartChgType(type: string): string {
  return type === 'sap_datasphere' ? 'sap' : type;
}

function mapSmartDatasource(raw: any, userId?: string): DataSource {
  const dsUuid = String(raw?.datasource_id ?? '');
  const row: DataSource = {
    id: uuidToInt(dsUuid),
    user_id: userId,
    name: String(raw?.name ?? 'Untitled'),
    type: fromSmartChgType(String(raw?.type ?? '')),
    is_active: Boolean(raw?.is_active),
    created_at: raw?.created_at,
  };
  (row as any).datasource_id = dsUuid;
  (row as any).description = raw?.description ?? undefined;
  return row;
}

async function defaultListDataSources(userId: string): Promise<DataSource[]> {
  const response = await api.get('/api/v1/datasources');
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => mapSmartDatasource(row, userId));
}

async function defaultCreateDataSource(request: DataSourceCreateRequest): Promise<DataSource> {
  const config = compactRecord({
    host: request.host,
    port: request.port,
    database_name: request.database_name,
    file_path: request.file_path,
  });
  const credentials = compactRecord({
    type: 'password',
    username: request.username,
    password: request.password,
  });
  const response = await api.post('/api/v1/datasources', {
    name: request.name,
    type: toSmartChgType(request.type),
    config: Object.keys(config).length ? config : undefined,
    credentials: Object.keys(credentials).length > 1 ? credentials : undefined,
  });
  return mapSmartDatasource(response.data, request.user_id);
}

async function defaultSetDataSourceActivation(
  userId: string,
  dataSourceId: number,
  isActive: boolean,
): Promise<DataSource> {
  const response = await api.patch(`/api/v1/datasources/${encodeURIComponent(String(dataSourceId))}/activation`, {
    is_active: isActive,
  });
  return mapSmartDatasource(response.data, userId);
}

async function defaultDeleteDataSource(_userId: string, dataSourceId: number): Promise<{ success: boolean; message: string }> {
  await api.delete(`/api/v1/datasources/${encodeURIComponent(String(dataSourceId))}`);
  return { success: true, message: 'Data source deleted successfully' };
}

async function unsupportedDataSourceAction<T>(action: string): Promise<T> {
  throw new Error(`${action} is not available from the current SmartCHG datasource catalog.`);
}

const defaultApiFunctions: DataSourceApiFunctions = {
  listDataSources: defaultListDataSources,
  createDataSource: defaultCreateDataSource,
  uploadExcelDataSource: () => unsupportedDataSourceAction<DataSource>('Excel upload'),
  uploadMultipleExcelDataSources: () => unsupportedDataSourceAction<DataSource>('Multi-file upload'),
  activateDataSource: (userId, dataSourceId) => defaultSetDataSourceActivation(userId, dataSourceId, true),
  deactivateDataSource: async (userId, dataSourceId) => {
    await defaultSetDataSourceActivation(userId, dataSourceId, false);
    return { success: true, message: 'Data source deactivated successfully' };
  },
  deleteDataSource: defaultDeleteDataSource,
  appendExcelSheets: () => unsupportedDataSourceAction<any>('Appending Excel sheets'),
  testDataSource: async () => ({
    success: false,
    message: 'Connection tests are not exposed by the current SmartCHG datasource catalog.',
  }),
  testAllDataSources: async () => ({}),
  getActiveDataSource: async (userId) => {
    const rows = await defaultListDataSources(userId);
    return rows.find((row) => row.is_active) ?? null;
  },
  getExcelSheets: async (dataSourceId) => ({
    data_source_id: dataSourceId,
    file_path: '',
    sheets: [],
  }),
  startDataSourceAnalysis: defaultStartDataSourceAnalysis,
  getActiveAnalyses: defaultGetActiveAnalyses,
  getLatestAnalyses: defaultGetLatestAnalyses,
  getColumnDescriptions: defaultGetColumnDescriptions,
};

interface DataSourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  dataSourceApi?: DataSourceApiFunctions;
  sapSource?: string;
  panelSide?: 'left' | 'right';
}

export function DataSourceModal({ open, onOpenChange, userId, dataSourceApi, sapSource = 'finance-v2', panelSide = 'right' }: DataSourceModalProps) {
  const dsApi = dataSourceApi || defaultApiFunctions;
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [activeDataSource, setActiveDataSource] = useState<DataSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDataSourceType, setSelectedDataSourceType] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [connectionStatuses, setConnectionStatuses] = useState<Record<number, DataSourceTestResponse>>({});
  const [checkingConnections, setCheckingConnections] = useState(false);
  const [excelSheets, setExcelSheets] = useState<Record<number, { name: string, columns: string[], column_count: number }[]>>({});
  const [loadingSheets, setLoadingSheets] = useState<Record<number, boolean>>({});
  const [analysisStatuses, setAnalysisStatuses] = useState<Record<number, DataSourceAnalysisStatus>>({});
  const [columnDescriptions, setColumnDescriptions] = useState<Record<number, ColumnDescription[]>>({});
  const [loadingColumns, setLoadingColumns] = useState<Record<number, boolean>>({});
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const { toast } = useToast();
  const hasSapRow = dataSources.some((d) => d.type === 'sap');

  // Form state for new data source
  const [formData, setFormData] = useState<DataSourceCreateRequest>({
    user_id: userId,
    name: '',
    type: 'postgres',
    host: '',
    port: 5432,
    username: '',
    password: '',
    database_name: '',
  });
  const [dataSourceDescription, setDataSourceDescription] = useState('');

  // Form state for Excel upload
  const [excelName, setExcelName] = useState('');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelFiles, setExcelFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, { status: 'uploading' | 'success' | 'error', message?: string }>>({});
  
  // Validation state
  const [nameValidationError, setNameValidationError] = useState<string | null>(null);
  const [excelNameValidationError, setExcelNameValidationError] = useState<string | null>(null);

  // Form state for appending sheets to existing Excel
  const [showAppendForm, setShowAppendForm] = useState<number | null>(null);
  const [appendFile, setAppendFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      loadDataSources();
      loadAnalysisStatuses(true); // Use latest analyses on initial load to include completed ones
    }
  }, [open, userId]);

  // Initiate the SAP OAuth Authorization Code flow.
  const handleConnectSap = async () => {
    try {
      await sapOAuthService.initiateConnection(sapSource);
    } catch (error: any) {
      toast({
        title: 'Connection Failed',
        description: error?.message || 'Failed to initiate SAP connection. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Re-run the SAP OAuth flow for an existing row. SAP redirects back to
  // /sap/callback which posts to /auth/sap/callback-exchange and
  // refreshes the canonical SAP token state through SapTokenProvider.
  const handleReconnectSap = async () => {
    try {
      await sapOAuthService.initiateConnection(sapSource);
    } catch (error: any) {
      toast({
        title: 'Reconnect Failed',
        description: error?.message || 'Failed to start SAP reconnect. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Poll for analysis status updates only when there are in-progress analyses
  useEffect(() => {
    if (!open) return;

    // Check if there are any in-progress analyses
    const hasInProgress = Object.values(analysisStatuses).some(
      status => status.status === 'in_progress' || status.status === 'pending'
    );

    if (!hasInProgress) return;

    // Set up polling interval (check every 3 seconds) only if there are in-progress analyses
    const pollInterval = setInterval(() => {
      loadAnalysisStatuses();
    }, 3000);

    // Cleanup interval on unmount or when modal closes
    return () => {
      clearInterval(pollInterval);
    };
  }, [open, analysisStatuses]);

  // Helper function to check if a name already exists
  const checkNameExists = (name: string): boolean => {
    if (!name || !name.trim()) return false;
    return dataSources.some(
      (ds) => ds.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
  };

  const loadDataSources = async () => {
    try {
      setLoading(true);
      // SmartCHG: source the list from the real /api/v1/datasources endpoint.
      // Mapping rules:
      //   - Synthesize a numeric `id` (modal uses it as a React key + for
      //     internal action lookups). Real UUID is preserved on
      //     `(source as any).datasource_id` for the manage dialog.
      //   - Display `type='sap_datasphere'` rows as `'sap'` so the
      //     small-cap badge matches the rest of the UI's vocabulary.
      //   - `active` = first row with `is_active=true` (single-active model).
      const mapped = await defaultListDataSources(userId);
      setDataSources(mapped);
      setActiveDataSource(mapped.find((m) => m.is_active) ?? null);
      // Re-validate names after loading data sources
      if (formData.name) {
        validateDatabaseName(formData.name);
      }
      if (excelName) {
        validateExcelName(excelName);
      }
    } catch (error) {
      console.error('Failed to load data sources:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data sources',
        variant: 'destructive',
      });
      setDataSources([]);
      setActiveDataSource(null);
    } finally {
      setLoading(false);
    }
  };

  // Validation functions for real-time checking
  const validateDatabaseName = (name: string) => {
    if (!name || !name.trim()) {
      setNameValidationError(null);
      return;
    }
    if (checkNameExists(name)) {
      setNameValidationError(`A data source with the name "${name}" already exists. Please use a different name.`);
    } else {
      setNameValidationError(null);
    }
  };

  const validateExcelName = (name: string) => {
    if (!name || !name.trim()) {
      setExcelNameValidationError(null);
      return;
    }
    if (checkNameExists(name)) {
      setExcelNameValidationError(`A data source with the name "${name}" already exists. Please use a different name.`);
    } else {
      setExcelNameValidationError(null);
    }
  };

  const loadAnalysisStatuses = async (useLatest: boolean = false) => {
    try {
      // Use getLatestAnalyses for initial load (includes completed), getActiveAnalyses for polling
      const analyses = useLatest ? await dsApi.getLatestAnalyses() : await dsApi.getActiveAnalyses();
      // Map analyses by data_source_id for quick lookup
      const statusMap: Record<number, DataSourceAnalysisStatus> = {};

      // Use functional update to access current state
      setAnalysisStatuses(prevStatuses => {
        analyses.forEach(analysis => {
          if (analysis.data_source_id) {
            const previousStatus = prevStatuses[analysis.data_source_id]?.status;
            const currentStatus = analysis.status;

            // Check if status changed from in_progress/pending to completed
            if (
              (previousStatus === 'in_progress' || previousStatus === 'pending') &&
              currentStatus === 'completed'
            ) {
              toast({
                title: 'Analysis Completed',
                description: `Analysis for data source has been completed successfully.`,
                variant: 'default',
              });
              // Load column descriptions only when status changes to completed.
              // Use the canonical UUID (analysis.analysis_id) — the synthetic
              // synthetic numeric ``analysis.id`` won't resolve on the
              // SmartCHG backend.
              if (analysis.analysis_id && !columnDescriptions[analysis.data_source_id]) {
                loadColumnDescriptions(analysis.analysis_id, analysis.data_source_id);
              }
            }

            // Check if status changed to failed
            if (
              (previousStatus === 'in_progress' || previousStatus === 'pending') &&
              currentStatus === 'failed'
            ) {
              toast({
                title: 'Analysis Failed',
                description: analysis.error_message || 'Analysis failed to complete.',
                variant: 'destructive',
              });
            }

            statusMap[analysis.data_source_id] = analysis;
          }
        });
        // Merge with previous statuses to keep completed ones that might not be in active list
        if (!useLatest) {
          Object.keys(prevStatuses).forEach(key => {
            const dataSourceId = parseInt(key);
            const prevStatus = prevStatuses[dataSourceId];
            // Keep completed/failed statuses if not in active list
            if (prevStatus && (prevStatus.status === 'completed' || prevStatus.status === 'failed')) {
              if (!statusMap[dataSourceId]) {
                statusMap[dataSourceId] = prevStatus;
              }
            }
          });
        }
        return statusMap;
      });
    } catch (error) {
      console.error('Failed to load analysis statuses:', error);
      // Don't show error toast - this is a background operation
    }
  };

  const loadColumnDescriptions = async (analysisId: string | number, dataSourceId: number) => {
    try {
      setLoadingColumns(prev => ({ ...prev, [dataSourceId]: true }));
      const columns = await dsApi.getColumnDescriptions(analysisId);
      setColumnDescriptions(prev => ({
        ...prev,
        [dataSourceId]: columns
      }));
    } catch (error) {
      console.error(`Failed to load column descriptions for data source ${dataSourceId}:`, error);
    } finally {
      setLoadingColumns(prev => ({ ...prev, [dataSourceId]: false }));
    }
  };

  const checkAllConnections = async (sources?: DataSource[]) => {
    const sourcesToCheck = sources || dataSources;
    if (sourcesToCheck.length === 0) return;

    try {
      setCheckingConnections(true);
      const statuses = await dsApi.testAllDataSources(userId);
      setConnectionStatuses(statuses);
    } catch (error) {
      console.error('Failed to check connections:', error);
    } finally {
      setCheckingConnections(false);
    }
  };

  const loadExcelSheets = async (dataSourceId: number) => {
    try {
      setLoadingSheets(prev => ({ ...prev, [dataSourceId]: true }));
      const sheetInfo = await dsApi.getExcelSheets(dataSourceId, userId);
      setExcelSheets(prev => ({
        ...prev,
        [dataSourceId]: sheetInfo.sheets
      }));
    } catch (error) {
      console.error(`Failed to load sheets for data source ${dataSourceId}:`, error);
    } finally {
      setLoadingSheets(prev => ({ ...prev, [dataSourceId]: false }));
    }
  };


  const handleCreateDataSource = async () => {
    try {
      setLoading(true);

      if (!formData.name || !formData.type) {
        toast({
          title: 'Validation Error',
          description: 'Name and type are required',
          variant: 'destructive',
        });
        return;
      }

      if (formData.type !== 'excel') {
        if (!formData.host || !formData.database_name) {
          toast({
            title: 'Validation Error',
            description: 'Host and database name are required for database sources',
            variant: 'destructive',
          });
          return;
        }
      }

      // Additional check (redundant but safe) - validation should have caught this already
      if (nameValidationError) {
        toast({
          title: 'Validation Error',
          description: nameValidationError,
          variant: 'destructive',
        });
        console.log('[DataSourceModal] Duplicate name detected, preventing data source creation:', formData.name);
        return;
      }

      const newDataSource = await dsApi.createDataSource(formData);

      if (newDataSource.id && formData.type !== 'excel') {
        try {
          const testResult = await dsApi.testDataSource(userId, newDataSource.id);
          setConnectionStatuses(prev => ({
            ...prev,
            [newDataSource.id!]: testResult
          }));
        } catch (error) {
          console.warn('Connection test failed after creation:', error);
        }
      }

      // Automatically start analysis for the new data source
      if (newDataSource.id) {
        try {
          const description = dataSourceDescription.trim() || `${formData.name} - ${formData.type} data source`;
          const dsId = (newDataSource as any).datasource_id || newDataSource.id;
          const result = await dsApi.startDataSourceAnalysis(dsId, userId, description);
          console.log('Analysis started automatically for data source:', dsId, 'Analysis ID:', result.analysis_id);
          // Analysis processing happens in background - status will be updated via polling
        } catch (error) {
          console.warn('Failed to start analysis automatically:', error);
          // Don't show error to user - analysis failure shouldn't block data source creation
        }
      }

      toast({
        title: 'Success',
        description: 'Data source created successfully. Analysis started automatically.',
      });

      setFormData({
        user_id: userId,
        name: '',
        type: 'postgres',
        host: '',
        port: 5432,
        username: '',
        password: '',
        database_name: '',
      });
      setDataSourceDescription('');
      setNameValidationError(null);
      setShowAddForm(false);
      setSelectedDataSourceType(null);
      await loadDataSources();
    } catch (error) {
      console.error('Failed to create data source:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create data source',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExcel = async () => {
    try {
      setLoading(true);

      // Handle multiple files
      const filesToUpload = excelFiles.length > 0 ? excelFiles : (excelFile ? [excelFile] : []);

      if (filesToUpload.length === 0) {
        const fileType = selectedDataSourceType === 'csv' ? 'CSV' : 'Excel';
        toast({
          title: 'Validation Error',
          description: `Please select at least one ${fileType} file`,
          variant: 'destructive',
        });
        return;
      }

      if (!excelName) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a data source name',
          variant: 'destructive',
        });
        return;
      }

      // Additional check (redundant but safe) - validation should have caught this already
      if (excelNameValidationError) {
        toast({
          title: 'Validation Error',
          description: excelNameValidationError,
          variant: 'destructive',
        });
        console.log('[DataSourceModal] Duplicate name detected, preventing Excel upload:', excelName);
        return;
      }

      // Set uploading status for all files
      filesToUpload.forEach(file => {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'uploading', message: filesToUpload.length > 1 ? 'Merging files...' : 'Uploading...' }
        }));
      });

      try {
        // If multiple files, merge them into one data source
        if (filesToUpload.length > 1) {
          const newDataSource = await dsApi.uploadMultipleExcelDataSources(userId, excelName, filesToUpload);

          // Mark all files as successful
          filesToUpload.forEach(file => {
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: 'success', message: 'Merged successfully' }
            }));
          });

          if (newDataSource.id) {
            try {
              const testResult = await dsApi.testDataSource(userId, newDataSource.id);
              setConnectionStatuses(prev => ({
                ...prev,
                [newDataSource.id!]: testResult
              }));
            } catch (error) {
              console.warn('Connection test failed after Excel upload:', error);
            }
            await loadExcelSheets(newDataSource.id);

            // Automatically start analysis for the new data source
            try {
              const sheetInfo = await dsApi.getExcelSheets(newDataSource.id, userId);
              const totalSheets = sheetInfo.sheets?.length || 0;
              const description = `${excelName} - Excel data source with ${totalSheets} sheet(s)`;
              const result = await dsApi.startDataSourceAnalysis(newDataSource.id, userId, description);
              console.log('Analysis started automatically for data source:', newDataSource.id, 'Analysis ID:', result.analysis_id);
              // Analysis processing happens in background - status will be updated via polling
            } catch (error) {
              console.warn('Failed to start analysis automatically:', error);
            }
          }

          toast({
            title: 'Upload Complete',
            description: `${filesToUpload.length} file(s) merged into one data source with all sheets. Analysis started automatically.`,
          });
        } else {
          // Single file upload
          const file = filesToUpload[0];
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'uploading', message: 'Uploading...' }
          }));

          const newDataSource = await dsApi.uploadExcelDataSource(userId, excelName, file);

          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'success', message: 'Uploaded successfully' }
          }));

          if (newDataSource.id) {
            try {
              const testResult = await dsApi.testDataSource(userId, newDataSource.id);
              setConnectionStatuses(prev => ({
                ...prev,
                [newDataSource.id!]: testResult
              }));
            } catch (error) {
              console.warn('Connection test failed after Excel upload:', error);
            }
            await loadExcelSheets(newDataSource.id);

            // Automatically start analysis for the new data source
            try {
              const description = dataSourceDescription.trim() || `${excelName} - ${selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} data source`;
              const result = await dsApi.startDataSourceAnalysis(newDataSource.id, userId, description);
              console.log('Analysis started automatically for data source:', newDataSource.id, 'Analysis ID:', result.analysis_id);
              // Analysis processing happens in background - status will be updated via polling
            } catch (error) {
              console.warn('Failed to start analysis automatically:', error);
            }
          }

          const fileType = selectedDataSourceType === 'csv' ? 'CSV' : 'Excel';
          toast({
            title: 'Upload Complete',
            description: `${fileType} file uploaded successfully. Analysis started automatically.`,
          });
        }

        // Reset form
        setExcelName('');
        setDataSourceDescription('');
        setExcelFile(null);
        setExcelFiles([]);
        setUploadProgress({});
        setExcelNameValidationError(null);
        setShowAddForm(false);
        setSelectedDataSourceType(null);
        await loadDataSources();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        filesToUpload.forEach(file => {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', message: errorMessage }
          }));
        });
        toast({
          title: 'Upload Failed',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (error) {
      const fileType = selectedDataSourceType === 'csv' ? 'CSV' : 'Excel';
      console.error(`Failed to upload ${fileType} file:`, error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : `Failed to upload ${fileType} file`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (id: number, uuid?: string) => {
    try {
      setLoading(true);
      if (uuid) {
        await api.patch(`/api/v1/datasources/${uuid}/activation`, { is_active: false });
      } else {
        await dsApi.deactivateDataSource(userId, id);
      }
      toast({
        title: 'Success',
        description: 'Data source deactivated successfully',
      });
      await loadDataSources();
    } catch (error) {
      console.error('Failed to deactivate data source:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to deactivate data source',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string, uuid?: string) => {
    if (!confirm(`Are you sure you want to delete the data source "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      if (uuid) {
        await api.delete(`/api/v1/datasources/${uuid}`);
      } else {
        await dsApi.deleteDataSource(userId, id);
      }
      toast({
        title: 'Success',
        description: 'Data source deleted successfully',
      });
      await loadDataSources();
    } catch (error) {
      console.error('Failed to delete data source:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete data source',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAppendSheets = async (dataSourceId: number) => {
    if (!appendFile) {
      toast({
        title: 'Validation Error',
        description: 'Please select an Excel file',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const result = await dsApi.appendExcelSheets(dataSourceId, userId, appendFile);

      let message = result.message;
      if (result.skipped_sheets && result.skipped_sheets.length > 0) {
        const skipped = result.skipped_sheets.map(s => `${s.original} → ${s.renamed}`).join(', ');
        message += ` Some sheets were renamed due to conflicts: ${skipped}`;
      }

      toast({
        title: 'Success',
        description: message,
      });

      setAppendFile(null);
      setShowAppendForm(null);
      await loadExcelSheets(dataSourceId);
      await loadDataSources();

      // Automatically start analysis when new sheets are added
      try {
        const source = dataSources.find(s => s.id === dataSourceId);
        if (source) {
          const sheetInfo = await dsApi.getExcelSheets(dataSourceId, userId);
          const totalSheets = sheetInfo.sheets?.length || 0;
          const description = `${source.name} - Excel data source with ${totalSheets} sheet(s)`;
          const result = await dsApi.startDataSourceAnalysis(dataSourceId, userId, description);
          console.log('Analysis started automatically after appending sheets:', dataSourceId, 'Analysis ID:', result.analysis_id);
          // Analysis processing happens in background - status will be updated via polling
          await loadAnalysisStatuses(); // Refresh status
        }
      } catch (error) {
        console.warn('Failed to start analysis after appending sheets:', error);
      }
    } catch (error) {
      console.error('Failed to append sheets:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to append sheets',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      setLoading(true);
      const activated = await dsApi.activateDataSource(userId, id);
      setActiveDataSource(activated);

      try {
        const testResult = await dsApi.testDataSource(userId, id);
        setConnectionStatuses(prev => ({
          ...prev,
          [id]: testResult
        }));
      } catch (error) {
        console.warn('Connection test failed after activation:', error);
      }

      toast({
        title: 'Success',
        description: `Data source "${activated.name}" is now active`,
      });
      await loadDataSources();
    } catch (error) {
      console.error('Failed to activate data source:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to activate data source',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalysis = async (id: number, name: string) => {
    try {
      setLoading(true);
      const source = dataSources.find(ds => ds.id === id);
      const description = source ? `${name} - ${source.type} data source` : `${name} - data source`;
      // SmartCHG keys datasources on UUIDs. The numeric ``id`` here is a
      // synthetic React key (see loadDataSources) — the real UUID was
      // stashed on ``(row as any).datasource_id`` for downstream calls.
      const dsUuid = (source as any)?.datasource_id || String(id);
      if (!dsUuid || /^\d+$/.test(String(dsUuid))) {
        toast({
          title: 'Cannot start analysis',
          description: 'This data source is missing its UUID. Reload the modal and try again.',
          variant: 'destructive',
        });
        return;
      }
      const result = await dsApi.startDataSourceAnalysis(dsUuid, userId, description);
      console.log('Analysis started for datasource UUID:', dsUuid, 'Analysis ID:', result.analysis_id);

      toast({
        title: 'Analysis Started',
        description: 'Data source analysis has been started. You can monitor progress below.',
      });

      // Refresh analysis statuses
      await loadAnalysisStatuses();
    } catch (error) {
      console.error('Failed to start analysis:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start analysis',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id: number) => {
    try {
      setTestingId(id);
      const result = await dsApi.testDataSource(userId, id);
      setConnectionStatuses(prev => ({
        ...prev,
        [id]: result
      }));
      toast({
        title: result.success ? 'Connection Successful' : 'Connection Failed',
        description: result.message,
        variant: result.success ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to test connection',
        variant: 'destructive',
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleAddDataSourceType = (type: string) => {
    setSelectedDataSourceType(type);
    setShowAddForm(true);
    setDataSourceDescription('');
    setNameValidationError(null);
    setExcelNameValidationError(null);
    if (type === 'excel' || type === 'csv') {
      setFormData({
        user_id: userId,
        name: '',
        type: type as any,
        host: '',
        port: 5432,
        username: '',
        password: '',
        database_name: '',
      });
      setExcelName('');
    } else {
      setFormData({
        user_id: userId,
        name: '',
        type: type as any,
        host: '',
        port: type === 'postgres' ? 5432 : type === 'sqlserver' ? 1433 : 3306,
        username: '',
        password: '',
        database_name: '',
      });
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      postgres: 'PostgreSQL',
      sqlserver: 'MySQL',
      // sap: 'SAP', // Commented out for now
      excel: 'Excel',
      csv: 'CSV',
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    if (type === 'excel') {
      return <FileSpreadsheet className="h-4 w-4" />;
    }
    if (type === 'csv') {
      return <FileSpreadsheet className="h-4 w-4" />;
    }
    if (type === 'sap') {
      return <Link className="h-4 w-4" />;
    }
    return <Server className="h-4 w-4" />;
  };

  const formatDetailLabel = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const isDateTimeDetail = (key: string) => {
    const normalizedKey = key.toLowerCase();
    return normalizedKey.endsWith('_at') || normalizedKey.endsWith('date') || normalizedKey.endsWith('time');
  };

  const formatDateTimeDetail = (value: string) => {
    const normalizedValue = value.replace(/\.(\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/, '.$1$2');
    const date = new Date(normalizedValue);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatDetailValue = (key: string, value: unknown): string => {
    if (value == null || value === '') return '-';
    if (Array.isArray(value)) return value.length > 0 ? value.map((item) => typeof item === 'object' ? JSON.stringify(item) : String(item)).join(', ') : '-';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' && isDateTimeDetail(key)) return formatDateTimeDetail(value);
    return String(value);
  };

  const getSourceDetails = (source: DataSource): Array<{ label: string; value: string }> => {
    const excludedKeys = new Set(['id', 'user_id', 'name', 'type', 'password', 'is_active', 'tables', 'datasource_id', 'created_at']);
    const sourceEntries: Array<[string, unknown]> = Object.keys(source).map((key) => [
      key,
      source[key as keyof DataSource],
    ]);

    return sourceEntries
      .filter(([key, value]) => !excludedKeys.has(key) && value != null && value !== '')
      .map(([key, value]) => ({ label: formatDetailLabel(key), value: formatDetailValue(key, value) }));
  };

  const getSourceStatus = (
    sourceId: number,
    status?: DataSourceTestResponse
  ): { label: string; message?: string; dotClassName: string; textClassName: string } => {
    if (checkingConnections || testingId === sourceId) {
      return {
        label: 'Connecting',
        dotClassName: 'bg-blue-500 animate-pulse',
        textClassName: 'text-blue-700 dark:text-blue-400',
      };
    }

    if (!status) {
      return {
        label: 'Not verified',
        dotClassName: 'bg-slate-400',
        textClassName: 'text-muted-foreground',
      };
    }

    if (status.success) {
      return {
        label: 'Connected',
        message: status.message || 'Connection verified',
        dotClassName: 'bg-emerald-500',
        textClassName: 'text-slate-600 dark:text-slate-300',
      };
    }

    return {
      label: 'Failed',
      message: status.message,
      dotClassName: 'bg-red-500',
      textClassName: 'text-red-700 dark:text-red-400',
    };
  };

  const getTypeColor = (type: string, isActive: boolean) => {
    const colors: Record<string, { border: string; bg: string; iconBg: string; accent: string }> = {
      excel: {
        border: isActive ? 'border-green-500/30' : 'border-green-200 dark:border-green-900/30',
        bg: isActive ? 'bg-green-50/50 dark:bg-green-950/20' : 'bg-white dark:bg-slate-900',
        iconBg: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400',
        accent: 'text-green-700 dark:text-green-400',
      },
      csv: {
        border: isActive ? 'border-blue-500/30' : 'border-blue-200 dark:border-blue-900/30',
        bg: isActive ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-white dark:bg-slate-900',
        iconBg: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400',
        accent: 'text-blue-700 dark:text-blue-400',
      },
      sap: {
        border: isActive ? 'border-purple-500/30' : 'border-purple-200 dark:border-purple-900/30',
        bg: isActive ? 'bg-purple-50/50 dark:bg-purple-950/20' : 'bg-white dark:bg-slate-900',
        iconBg: 'bg-primary/10 text-primary dark:bg-primary/20',
        accent: 'text-primary',
      },
      postgres: {
        border: isActive ? 'border-indigo-500/30' : 'border-indigo-200 dark:border-indigo-900/30',
        bg: isActive ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : 'bg-white dark:bg-slate-900',
        iconBg: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400',
        accent: 'text-indigo-700 dark:text-indigo-400',
      },
      sqlserver: {
        border: isActive ? 'border-orange-500/30' : 'border-orange-200 dark:border-orange-900/30',
        bg: isActive ? 'bg-orange-50/50 dark:bg-orange-950/20' : 'bg-white dark:bg-slate-900',
        iconBg: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400',
        accent: 'text-orange-700 dark:text-orange-400',
      },
    };
    return colors[type] || {
      border: isActive ? 'border-slate-500/30' : 'border-slate-200 dark:border-slate-800',
      bg: isActive ? 'bg-slate-50/50 dark:bg-slate-950/20' : 'bg-white dark:bg-slate-900',
      iconBg: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
      accent: 'text-slate-700 dark:text-slate-300',
    };
  };

  const toggleCardExpansion = (sourceId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sourceId)) {
        newSet.delete(sourceId);
      } else {
        newSet.add(sourceId);
      }
      return newSet;
    });
  };

  const renderDataSourceCard = (source: DataSource, index: number) => {
    const sourceId = source.id ?? index;
    const connectionStatus = connectionStatuses[sourceId];
    const sourceStatus = getSourceStatus(sourceId, connectionStatus);
    const details = getSourceDetails(source);
    const typeColors = getTypeColor(source.type, Boolean(source.is_active));
    const isExpanded = expandedCards.has(sourceId);
    const sheetRows = source.tables || excelSheets[sourceId] || [];
    const isVerified = connectionStatus?.success === true;
    const isFailed = connectionStatus?.success === false;
    const createdAtText = source.created_at ? formatDetailValue('created_at', source.created_at) : '';
    const hasExpandableContent = details.length > 0 || sheetRows.length > 0 || showAppendForm === sourceId;

    return (
      <div
        key={sourceId}
        className={`rounded-xl border bg-white p-4 shadow-[0_6px_18px_rgba(15,23,42,0.05)] transition-shadow hover:shadow-[0_10px_24px_rgba(15,23,42,0.07)] dark:bg-slate-950 ${
          source.type === 'sap' ? 'border-primary/25 dark:border-primary/35' : 'border-slate-200 dark:border-slate-800'
        }`}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${typeColors.iconBg} [&_svg]:h-5 [&_svg]:w-5`}>
            {getTypeIcon(source.type)}
          </div>

          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <h3 className="break-words text-sm font-semibold leading-tight text-slate-950 dark:text-slate-50">{source.name}</h3>
              <span className="rounded-md border border-primary/15 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {getTypeLabel(source.type)}
              </span>
              <span className="hidden h-4 w-px bg-slate-200 dark:bg-slate-800 sm:inline-block" />
              <span className={`inline-flex flex-col gap-0.5 text-[11px] font-medium ${sourceStatus.textClassName}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${sourceStatus.dotClassName}`} />
                  {sourceStatus.label}
                </span>
                {createdAtText && (
                  <span className="pl-3 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
                    Created {createdAtText}
                  </span>
                )}
              </span>
              {hasExpandableContent && (
                <button
                  type="button"
                  onClick={() => toggleCardExpansion(sourceId)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                  title={isExpanded ? 'Collapse details' : 'Expand details'}
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                >
                  {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
            {sourceStatus.message && (
              <p className={`inline-flex w-fit items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-semibold ${
                isVerified
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                  : isFailed
                    ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300'
              }`}>
                {isVerified ? <CheckCircle2 className="h-3.5 w-3.5" /> : isFailed ? <AlertCircle className="h-3.5 w-3.5" /> : null}
                {sourceStatus.message}
              </p>
            )}
          </div>

          <div className="col-span-2 flex flex-wrap items-center justify-end gap-2 lg:col-span-1" onClick={(e) => e.stopPropagation()}>
            {source.type === 'sap' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReconnectSap}
                disabled={loading}
                title="Reconnect to SAP Datasphere"
                className="h-8 gap-1.5 rounded-md border-slate-200 bg-white px-3 text-xs font-semibold text-slate-950 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50 dark:hover:bg-slate-900 [&_svg]:h-3.5 [&_svg]:w-3.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Reconnect
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete(sourceId, source.name, (source as any).datasource_id)}
              disabled={loading}
              title="Delete data source"
              className="h-8 w-8 rounded-md border-red-200 bg-white p-0 text-red-600 shadow-sm hover:bg-red-50 hover:text-red-700 dark:border-red-900/50 dark:bg-slate-950 dark:text-red-400 dark:hover:bg-red-950/25 [&_svg]:h-3.5 [&_svg]:w-3.5"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {isExpanded && hasExpandableContent && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40">
            {details.length > 0 ? (
              <dl className="grid gap-3 sm:grid-cols-2 sm:divide-x sm:divide-slate-200 dark:sm:divide-slate-800">
                {details.map((detail) => (
                  <div key={detail.label} className="min-w-0 px-2 first:pl-0 sm:pl-4">
                    <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400">{detail.label}</dt>
                    <dd className="mt-1.5 break-words text-sm font-medium text-slate-950 dark:text-slate-50">{detail.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">No additional details available.</p>
            )}

            {sheetRows.length > 0 && (
              <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-foreground">
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  {formatDetailLabel(source.type === 'csv' ? 'tables' : 'sheets')} ({sheetRows.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  <TooltipProvider>
                    {sheetRows.map((sheet, idx) => (
                      <Tooltip key={`${sheet.name}-${idx}`}>
                        <TooltipTrigger asChild>
                          <div className="cursor-help rounded-md border bg-background px-2.5 py-1.5 text-xs">
                            <span className="font-medium">{sheet.name}</span>
                            <span className="ml-2 text-muted-foreground">{sheet.column_count} cols</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-sm">
                          <div className="space-y-2">
                            <div className="font-semibold text-sm">{sheet.name}</div>
                            {sheet.columns && sheet.columns.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {sheet.columns.map((column) => (
                                  <span key={column} className="rounded border bg-muted px-1.5 py-0.5 text-xs">{column}</span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-muted-foreground">{sheet.column_count} columns</div>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                </div>
              </div>
            )}

            {source.type === 'excel' && showAppendForm === sourceId && (
              <div className="mt-4 rounded-lg border bg-background p-4">
                <Label className="text-xs font-semibold">Upload Excel file with new sheets</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  className="mt-2 text-xs"
                  onChange={(e) => setAppendFile(e.target.files?.[0] || null)}
                />
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-8 text-xs"
                    onClick={() => handleAppendSheets(sourceId)}
                    disabled={loading || !appendFile}
                  >
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FilePlus className="h-3 w-3" />}
                    Add sheets
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs"
                    onClick={() => {
                      setShowAppendForm(null);
                      setAppendFile(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDataSourceActions = (className: string) => (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="sm"
            className="h-8 gap-1.5 rounded-md px-3 text-xs font-semibold shadow-sm [&_svg]:h-3.5 [&_svg]:w-3.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem disabled className="text-muted-foreground">
            <Server className="h-4 w-4 mr-2" />
            PostgreSQL
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="text-muted-foreground">
            <Server className="h-4 w-4 mr-2" />
            MySQL
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleConnectSap}
            disabled={hasSapRow}
            className={hasSapRow ? 'text-muted-foreground' : undefined}
            title={hasSapRow ? 'SAP Datasphere is already connected. Use Reconnect or Delete on the row below.' : undefined}
          >
            <Link className="h-4 w-4 mr-2" />
            SAP Datasphere
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled className="text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel File
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            CSV File
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={panelSide}
        className="flex h-dvh w-[88vw] max-w-[560px] flex-col overflow-hidden border-slate-200 bg-white p-0 shadow-2xl dark:border-slate-800 dark:bg-slate-950 sm:max-w-[560px]"
        closeClassName="right-5 top-5 rounded-md text-slate-600 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-50 [&>svg]:h-5 [&>svg]:w-5"
      >
        <SheetHeader className="flex-shrink-0 border-b border-slate-200 bg-white px-5 py-4 pr-14 text-left dark:border-slate-800 dark:bg-slate-950 sm:px-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2.5 text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
              <Database className="h-5 w-5" />
              Data Source Manager
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className={dataSources.length === 0 && !showAddForm && !loading
          ? "flex flex-1 items-center justify-center overflow-y-auto bg-white px-5 py-6 dark:bg-slate-950 sm:px-6"
          : "flex-1 overflow-y-auto space-y-4 bg-white px-5 py-5 dark:bg-slate-950 sm:px-6"
        }>
          {/* Add Data Source Form */}
          {showAddForm && (
            <div className="p-5 border rounded-lg bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">
                  {selectedDataSourceType === 'excel' || selectedDataSourceType === 'csv'
                    ? `Upload ${selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} File${excelFiles.length > 1 ? 's' : ''}`
                    : `Add ${getTypeLabel(selectedDataSourceType || '')} Data Source`}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedDataSourceType(null);
                    setExcelName('');
                    setExcelFile(null);
                    setExcelFiles([]);
                    setUploadProgress({});
                    setExcelNameValidationError(null);
                    setFormData({
                      user_id: userId,
                      name: '',
                      type: 'postgres',
                      host: '',
                      port: 5432,
                      username: '',
                      password: '',
                      database_name: '',
                    });
                    setDataSourceDescription('');
                    setNameValidationError(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {(selectedDataSourceType === 'excel' || selectedDataSourceType === 'csv') ? (
                <div className="space-y-4">
                  <div>
                    <Label>Data Source Name</Label>
                    <Input
                      value={excelName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setExcelName(newName);
                        validateExcelName(newName);
                      }}
                      placeholder={`My ${selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} Data Source`}
                      className={`mt-1 ${excelNameValidationError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    />
                    {excelNameValidationError && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {excelNameValidationError}
                      </p>
                    )}
                    {excelFiles.length > 1 && !excelNameValidationError && (
                      <p className="text-xs text-muted-foreground mt-1">
                        All selected files will be merged into one data source. All {selectedDataSourceType === 'csv' ? 'tables' : 'sheets'} from all files will be available.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>
                      Description <span className="text-muted-foreground text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      value={dataSourceDescription}
                      onChange={(e) => setDataSourceDescription(e.target.value)}
                      placeholder="Describe what this data source contains, its purpose, and any relevant context. For example: 'This is a sales database containing customer transactions, product information, and sales team data from 2020-2024.'"
                      rows={3}
                      className="mt-1 resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This description will help the AI understand the context of your data and generate more accurate column descriptions.
                    </p>
                  </div>
                  <div>
                    <Label>{selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} File(s) - Select multiple files to upload</Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept={selectedDataSourceType === 'csv' ? '.csv' : '.xlsx,.xls'}
                        multiple
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files || []);
                          if (newFiles.length > 0) {
                            // Validate file types match
                            const isCsvType = selectedDataSourceType === 'csv';
                            const invalidFiles = newFiles.filter(f => {
                              const isExcel = f.name.endsWith('.xlsx') || f.name.endsWith('.xls');
                              const isCsv = f.name.endsWith('.csv');
                              if (isCsvType) {
                                return !isCsv;
                              } else {
                                return !isExcel;
                              }
                            });

                            if (invalidFiles.length > 0) {
                              toast({
                                title: 'Validation Error',
                                description: `Please select only ${isCsvType ? 'CSV' : 'Excel'} files`,
                                variant: 'destructive',
                              });
                              e.target.value = '';
                              return;
                            }

                            // Combine existing files with new files, avoiding duplicates
                            const existingFileNames = new Set(excelFiles.map(f => `${f.name}-${f.size}`));
                            const uniqueNewFiles = newFiles.filter(f => !existingFileNames.has(`${f.name}-${f.size}`));

                            if (uniqueNewFiles.length > 0) {
                              setExcelFiles(prev => [...prev, ...uniqueNewFiles]);
                              if (!excelFile) {
                                setExcelFile(uniqueNewFiles[0]); // Keep for backward compatibility
                              }
                            } else {
                              toast({
                                title: 'Info',
                                description: 'All selected files are already in the list',
                                variant: 'default',
                              });
                            }
                            // Reset the input so the same file can be selected again if needed
                            e.target.value = '';
                          }
                        }}
                        className="mt-1"
                      />
                      {excelFiles.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setExcelFiles([]);
                            setExcelFile(null);
                            setUploadProgress({});
                          }}
                          className="h-8 w-full text-xs"
                        >
                          Clear All Files
                        </Button>
                      )}
                    </div>
                    {excelFiles.length > 0 && (
                      <div className="mt-2 space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          Selected {excelFiles.length} file(s):
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {excelFiles.map((file, idx) => {
                            const progress = uploadProgress[file.name];
                            return (
                              <div
                                key={idx}
                                className={`text-xs p-2 rounded border flex items-center justify-between ${progress?.status === 'success' ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :
                                  progress?.status === 'error' ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800' :
                                    progress?.status === 'uploading' ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800' :
                                      'bg-muted border-border'
                                  }`}
                              >
                                <span className="truncate flex-1">{file.name}</span>
                                <div className="flex items-center gap-2">
                                  {progress && (
                                    <span className={`flex items-center gap-1 ${progress.status === 'success' ? 'text-green-600 dark:text-green-400' :
                                      progress.status === 'error' ? 'text-red-600 dark:text-red-400' :
                                        'text-blue-600 dark:text-blue-400'
                                      }`}>
                                      {progress.status === 'uploading' && <Loader2 className="h-3 w-3 animate-spin" />}
                                      {progress.status === 'success' && <Check className="h-3 w-3" />}
                                      {progress.status === 'error' && <AlertCircle className="h-3 w-3" />}
                                      <span className="text-xs">{progress.message}</span>
                                    </span>
                                  )}
                                  {!progress && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 text-red-600 hover:text-red-700"
                                      onClick={() => {
                                        setExcelFiles(prev => prev.filter((_, i) => i !== idx));
                                        if (excelFiles[idx] === excelFile && excelFiles.length > 1) {
                                          setExcelFile(excelFiles[0] === excelFile ? excelFiles[1] : excelFiles[0]);
                                        } else if (excelFiles.length === 1) {
                                          setExcelFile(null);
                                        }
                                      }}
                                      title="Remove file"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleUploadExcel}
                    disabled={loading || excelFiles.length === 0 || (!excelName && excelFiles.length === 1) || !!excelNameValidationError}
                    className="h-8 w-full text-xs [&_svg]:h-3.5 [&_svg]:w-3.5"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        Uploading {excelFiles.length} file(s)...
                      </>
                    ) : (
                      <>
                        <Upload className="h-3.5 w-3.5 mr-2" />
                        Upload {excelFiles.length > 1 ? `${excelFiles.length} ${selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} Files` : `${selectedDataSourceType === 'csv' ? 'CSV' : 'Excel'} File`}
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setFormData({ ...formData, name: newName });
                        validateDatabaseName(newName);
                      }}
                      placeholder="My Database"
                      className={`mt-1 ${nameValidationError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    />
                    {nameValidationError && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {nameValidationError}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Host</Label>
                    <Input
                      value={formData.host}
                      onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                      placeholder="localhost or IP address"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 5432 })}
                      placeholder="5432"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Database Name</Label>
                    <Input
                      value={formData.database_name}
                      onChange={(e) => setFormData({ ...formData, database_name: e.target.value })}
                      placeholder="database_name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="username"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="password"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>
                      Description <span className="text-muted-foreground text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      value={dataSourceDescription}
                      onChange={(e) => setDataSourceDescription(e.target.value)}
                      placeholder="Describe what this data source contains, its purpose, and any relevant context. For example: 'This is a sales database containing customer transactions, product information, and sales team data from 2020-2024.'"
                      rows={3}
                      className="mt-1 resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1 mb-4">
                      This description will help the AI understand the context of your data and generate more accurate column descriptions.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Button
                      onClick={handleCreateDataSource}
                      disabled={loading || !!nameValidationError}
                      className="h-8 w-full text-xs [&_svg]:h-3.5 [&_svg]:w-3.5"
                    >
                      {loading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-2" />}
                      Create Data Source
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {loading && dataSources.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : dataSources.length === 0 ? (
            !showAddForm && (
              <div className="flex w-full max-w-sm flex-col items-center justify-center gap-4 rounded-xl border border-slate-200 bg-white px-6 py-8 text-center shadow-[0_6px_18px_rgba(15,23,42,0.05)] dark:border-slate-800 dark:bg-slate-950">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
                  <Database className="h-6 w-6" />
                </div>
                <div className="space-y-1 text-muted-foreground">
                  <p className="text-sm font-semibold text-slate-950 dark:text-slate-50">No data sources configured</p>
                  <p className="text-xs">Click "Add Data Source" to get started</p>
                </div>
                {renderDataSourceActions("flex flex-col items-center gap-3 pt-1 sm:flex-row")}
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-50">
                  All Data Sources ({dataSources.length})
                </h3>
              </div>
              <div className="space-y-3">
                {dataSources.map(renderDataSourceCard)}
              </div>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5 text-xs font-medium text-slate-500 dark:text-slate-400">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary dark:bg-primary/20">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <span>Your connections are secure and encrypted.</span>
            </div>
            {dataSources.length > 0 && !showAddForm && renderDataSourceActions("flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end")}
          </div>
        </div>
      </SheetContent>
    </Sheet>

    </>
  );
}
